import type { NotificationPort, NotificationTone, NotificationAction } from '../../domain/ports/NotificationPort.ts';

/** Material / Android–style snackbar (bottom, safe area, icon + text). */
export class DomToastNotificationAdapter implements NotificationPort {
  private static instance: DomToastNotificationAdapter | null = null;

  private constructor() {}

  static getInstance(): DomToastNotificationAdapter {
    DomToastNotificationAdapter.instance ??= new DomToastNotificationAdapter();
    return DomToastNotificationAdapter.instance;
  }

  show(message: string, type: NotificationTone = 'info', duration: number = 4000, action?: NotificationAction): void {
    try {
      for (const el of document.querySelectorAll('[data-md-snackbar]')) {
        if (el instanceof HTMLDialogElement && el.open) {
          try {
            el.close();
          } catch {
            /* showModal may be unavailable in test envs. */
          }
        }
        el.remove();
      }

      const host = document.createElement('dialog');
      host.dataset.mdSnackbar = '';
      host.className = 'md-snackbar';
      host.setAttribute('role', 'status');
      host.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

      const surface = document.createElement('div');
      surface.className = `md-snackbar__surface md-snackbar__surface--${type}`;

      const iconWrap = document.createElement('span');
      iconWrap.className = 'md-snackbar__icon';
      iconWrap.setAttribute('aria-hidden', 'true');
      iconWrap.appendChild(DomToastNotificationAdapter.iconSvg(type));

      const text = document.createElement('p');
      text.className = 'md-snackbar__text';
      text.textContent = message;

      surface.append(iconWrap, text);
      if (action) {
        if (action.onClick) {
          const actionButton = document.createElement('button');
          actionButton.type = 'button';
          actionButton.className = 'md-snackbar__action';
          actionButton.textContent = action.label;
          actionButton.addEventListener('click', (ev) => {
            ev.stopPropagation();
            dismiss();
            action.onClick?.();
          });
          surface.appendChild(actionButton);
        } else if (action.href) {
          const actionLink = document.createElement('a');
          actionLink.className = 'md-snackbar__action';
          actionLink.href = action.href;
          actionLink.textContent = action.label;
          surface.appendChild(actionLink);
        }
      }
      host.appendChild(surface);
      document.body.appendChild(host);

      requestAnimationFrame(() => {
        try {
          host.showModal();
        } catch {
          try {
            host.show();
          } catch {
            /* Test envs may lack modal APIs. */
          }
        }
        host.classList.add('md-snackbar--visible');
      });

      const dismissMs = 320;
      let hideTimeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
      let removeTimeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;

      const dismiss = (): void => {
        if (hideTimeoutId !== undefined) {
          globalThis.clearTimeout(hideTimeoutId);
          hideTimeoutId = undefined;
        }
        if (removeTimeoutId !== undefined) return;
        host.classList.remove('md-snackbar--visible');
        removeTimeoutId = globalThis.setTimeout(() => {
          if (host.open) {
            try {
              host.close();
            } catch {
              /* noop */
            }
          }
          host.remove();
        }, dismissMs);
      };

      surface.addEventListener('click', dismiss);
      hideTimeoutId = globalThis.setTimeout(dismiss, duration);
    } catch (e) {
      // oxlint-disable-next-line no-console -- last resort if the DOM cannot show toasts
      console.warn('[notifications] show failed', e, message);
    }
  }

  private static iconPathD(type: NotificationTone): string {
    if (type === 'success') {
      return 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z';
    }
    if (type === 'error') {
      return 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z';
    }
    if (type === 'warning') {
      return 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z';
    }
    return 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z';
  }

  private static iconSvg(type: NotificationTone): SVGSVGElement {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'currentColor');
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', DomToastNotificationAdapter.iconPathD(type));
    svg.appendChild(path);
    return svg;
  }

  success(message: string, duration?: number, action?: NotificationAction): void {
    this.show(message, 'success', duration, action);
  }

  error(message: string, duration?: number): void {
    this.show(message, 'error', duration);
  }

  info(message: string, duration?: number): void {
    this.show(message, 'info', duration);
  }

  warning(message: string, duration?: number): void {
    this.show(message, 'warning', duration);
  }
}
